import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { compact, formatNum } from "../lib/format";
import "./BuyerSalesExplorer.css";

export default function BuyerSalesExplorer({ rows, capped, fmt, onDrillDown }) {
  const [limit, setLimit] = useState("10");
  const [view, setView] = useState("chart");
  const [search, setSearch] = useState("");
  const codes = new Map();
  rows.forEach(row => {
    const code = String(row.display_label || row.label).trim();
    codes.set(code, (codes.get(code) || 0) + 1);
  });
  const labelFor = row => {
    const code = String(row.display_label || row.label).trim();
    return codes.get(code) > 1 ? row.label : code;
  };
  const selected = limit === "all" && !capped ? rows : rows.slice(0, Number(limit) || 20);
  const labels = new Map(rows.map(row => [row.label, labelFor(row)]));
  const query = search.trim().toLowerCase();
  const matches = rows.filter(row => !query || `${row.label} ${row.display_label || ""}`.toLowerCase().includes(query));
  const money = value => formatNum(value, fmt || { prefix: "$", decimals: 0 });
  const shortMoney = value => `${fmt?.prefix || "$"}${compact(value)}`;
  const totalText = `${capped ? "at least " : ""}${rows.length}`;
  const tick = ({ x, y, payload }) => {
    const full = labels.get(payload.value) || payload.value;
    const short = full.length > 18 ? full.slice(0, 17) + "…" : full;
    return <g transform={`translate(${x},${y})`}><title>{payload.value}</title>
      <text dy={14} textAnchor="end" transform="rotate(-30)" fill="#475569" fontSize={11}>{short}</text></g>;
  };

  return <div className="buyer-sales-explorer">
    <div className="buyer-sales-controls">
      <strong aria-live="polite">{view === "chart"
        ? `Showing ${selected.length === rows.length && !capped ? "all" : "top"} ${selected.length} of ${totalText} consignees`
        : `${matches.length} matches in ${totalText} consignees`}</strong>
      <div className="buyer-sales-actions">
        <label>Show <select value={limit} onChange={e => setLimit(e.target.value)}>
          <option value="10">Top 10</option><option value="20">Top 20</option>
          <option value="all" disabled={capped}>All</option>
        </select></label>
        <button type="button" aria-pressed={view === "chart"} onClick={() => setView("chart")}>Chart</button>
        <button type="button" aria-pressed={view === "list"} onClick={() => setView("list")}>{capped ? "Loaded list" : "Full list"}</button>
      </div>
    </div>
    <p className="buyer-sales-note">Ranked by sales value (USD) for the current filters. Full names are available on hover and in the list.</p>
    {capped && <p role="status">The 1,000-group display limit was reached. Narrow the filters to view a complete list.</p>}
    {!rows.length ? <p>No consignees match the current filters.</p> : view === "chart" ?
      <div className="buyer-sales-scroll">
        <div style={{ width: "100%", minWidth: Math.max(700, selected.length * 115 + 100), height: 390 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={selected} margin={{ top: 32, right: 25, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="2 3" vertical={false} stroke="#e8edf4" />
              <XAxis dataKey="label" interval={0} tick={tick} height={100} />
              <YAxis width={80} tickFormatter={shortMoney} tick={{ fontSize: 11 }} />
              <Tooltip content={({ active, payload }) => active && payload?.length ?
                <div className="buyer-sales-tooltip"><strong>{payload[0].payload.label}</strong><div>{money(payload[0].value)}</div></div> : null} />
              <Bar dataKey="value" fill="#1e5eff" radius={[5,5,0,0]} maxBarSize={64}
                cursor={onDrillDown ? "pointer" : "default"}
                onClick={entry => onDrillDown?.(entry?.payload || entry)}>
                <LabelList dataKey="value" position="top" formatter={shortMoney} style={{ fontSize: 11, fill: "#334155", fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div> :
      <div>
        <label className="buyer-sales-search">Search all loaded consignees
          <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Full name or stored code" />
        </label>
        <div className="buyer-sales-table"><table>
          <thead><tr><th>Consignee</th><th>Stored code</th><th>Sales value (USD)</th></tr></thead>
          <tbody>{matches.map(row => <tr key={row.label}>
            <td><button type="button" disabled={!onDrillDown} onClick={() => onDrillDown?.(row)}>{row.label}</button></td>
            <td>{row.display_label && row.display_label !== row.label ? row.display_label : "—"}</td>
            <td>{money(row.value)}</td>
          </tr>)}</tbody>
        </table>{!matches.length && <p>No matching consignees.</p>}</div>
      </div>}
  </div>;
}
